// src/hooks/useBooks.js
// Fetches books from Cloudflare D1 database APIs.
// Also handles client-side search, genre filter, and sort.

import { useState, useEffect, useMemo } from "react";
import { fallbackBooks } from "../data/fallbackBooks";
import { useAuth } from "../context/AuthContext";
import { getAuthToken } from "../utils/authToken";

// ── API Fetcher for Cloudflare D1 ──────────────────────────
export function useBooks() {
  const { user } = useAuth();
  const [books, setBooks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  function triggerRefresh() {
    setRefreshTrigger((t) => t + 1);
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch("/api/books")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load books. Status: ${res.status}`);
        }
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          return res.text().then((body) => {
            if (body.trim().startsWith("<!DOCTYPE") || body.trim().startsWith("<html")) {
              console.warn(
                "[Books] /api/books returned the React HTML shell. Using local fallback books for development."
              );
              return fallbackBooks;
            }
            throw new Error("Books API returned a non-JSON response.");
          });
        }
        return res.json();
      })
      .then((data) => {
        if (active) {
          setBooks(data);
          setLoading(false);
          setError(null);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshTrigger]);

  // Client-side visual increment after trigger download
  function recordDownload(bookId) {
    setBooks((prev) =>
      prev.map((b) =>
        b.id === bookId ? { ...b, downloadCount: (b.downloadCount || 0) + 1 } : b
      )
    );
  }

  // Create Book
  async function addBook(bookData) {
    const token = user ? await getAuthToken() : "";
    const res = await fetch("/api/books", {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify(bookData)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to create book");
    }
    triggerRefresh();
    return res.json();
  }

  // Update Book
  async function updateBook(bookId, bookData) {
    const token = user ? await getAuthToken() : "";
    const res = await fetch(`/api/books/${bookId}`, {
      method: "PUT",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify(bookData)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to update book");
    }
    triggerRefresh();
    return res.json();
  }

  // Delete Book
  async function deleteBook(bookId) {
    const token = user ? await getAuthToken() : "";
    const res = await fetch(`/api/books/${bookId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to delete book");
    }
    triggerRefresh();
    return res.json();
  }

  return { books, loading, error, recordDownload, addBook, updateBook, deleteBook, refreshBooks: triggerRefresh };
}

// ── Client-side filter + sort (no extra database reads) ──
export function useFilteredBooks(books) {
  const [search, setSearch]   = useState("");
  const [genre, setGenre]     = useState("All");
  const [sortBy, setSortBy]   = useState("title");

  const genres = useMemo(() => {
    const unique = [...new Set(books.map((b) => b.genre))].sort();
    return ["All", ...unique];
  }, [books]);

  const filtered = useMemo(() => {
    let list = [...books];

    if (genre !== "All") {
      list = list.filter((b) => b.genre === genre);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (b) =>
          b.title?.toLowerCase().includes(q) ||
          b.author?.toLowerCase().includes(q) ||
          b.genre?.toLowerCase().includes(q) ||
          b.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      if (sortBy === "title")     return a.title.localeCompare(b.title);
      if (sortBy === "author")    return a.author.localeCompare(b.author);
      if (sortBy === "rating")    return b.rating - a.rating;
      if (sortBy === "pages")     return a.pages - b.pages;
      if (sortBy === "downloads") return (b.downloadCount || 0) - (a.downloadCount || 0);
      return 0;
    });

    return list;
  }, [books, search, genre, sortBy]);

  return { filtered, genres, search, setSearch, genre, setGenre, sortBy, setSortBy };
}
